import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Code2, Copy, ArrowLeft, ExternalLink } from 'lucide-react';

const SITE_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminEmbeds() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [limit, setLimit] = useState(5);
  const [interval, setInterval] = useState(6000);
  const [height, setHeight] = useState(420);

  if (user && !user.is_admin) {
    navigate('/');
    return null;
  }

  const embedUrl = `${SITE_URL}/embed/hero?limit=${limit}&interval=${interval}`;

  const iframeHtml = `<iframe
  src="${embedUrl}"
  width="100%"
  height="${height}"
  style="border:0;border-radius:12px;max-width:100%;display:block;"
  loading="lazy"
  title="Contos em destaque · Intimate.pt"
  allow="autoplay"
  referrerpolicy="no-referrer-when-downgrade"
  sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"></iframe>`;

  const wpShortcodeNote = `[iframe src="${embedUrl}" width="100%" height="${height}px"]`;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copiado!`),
      () => toast.error('Falha a copiar')
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 pt-24 pb-12">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate('/admin')} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4 mr-2" />Voltar
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-ruby/20 flex items-center justify-center">
              <Code2 className="w-6 h-6 text-ruby" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Embeds para sites externos</h1>
              <p className="text-muted-foreground text-sm">Código pronto a colar em WordPress, Wix, ou qualquer site HTML.</p>
            </div>
          </div>
        </div>

        <Card className="bg-surface border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">Slide dos Contos</CardTitle>
            <p className="text-sm text-muted-foreground">
              Slide automático com as histórias mais recentes. Cliques abrem a Intimate.pt num novo separador.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Settings */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">N.º de contos</Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={limit}
                  onChange={(e) => setLimit(Math.max(1, Math.min(10, Number(e.target.value) || 5)))}
                  className="bg-background border-border"
                  data-testid="embed-limit"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Intervalo (ms)</Label>
                <Input
                  type="number"
                  min={2000}
                  step={500}
                  value={interval}
                  onChange={(e) => setInterval(Math.max(2000, Number(e.target.value) || 6000))}
                  className="bg-background border-border"
                  data-testid="embed-interval"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Altura (px)</Label>
                <Input
                  type="number"
                  min={280}
                  step={20}
                  value={height}
                  onChange={(e) => setHeight(Math.max(280, Number(e.target.value) || 420))}
                  className="bg-background border-border"
                  data-testid="embed-height"
                />
              </div>
            </div>

            {/* URL */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">URL do embed</Label>
                <a
                  href={embedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-ruby hover:underline flex items-center gap-1"
                >
                  Pré-visualizar <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={embedUrl}
                  className="bg-background border-border font-mono text-xs"
                  data-testid="embed-url"
                />
                <Button
                  variant="outline"
                  className="border-border shrink-0"
                  onClick={() => copy(embedUrl, 'URL')}
                  data-testid="embed-copy-url"
                >
                  <Copy className="w-4 h-4 mr-2" />Copiar
                </Button>
              </div>
            </div>

            <Tabs defaultValue="iframe" className="w-full">
              <TabsList className="bg-background border border-border">
                <TabsTrigger value="iframe">HTML / iframe</TabsTrigger>
                <TabsTrigger value="wp">WordPress (shortcode)</TabsTrigger>
                <TabsTrigger value="preview">Pré-visualização</TabsTrigger>
              </TabsList>

              <TabsContent value="iframe" className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Cole este código num bloco HTML personalizado (Gutenberg → Bloco "HTML personalizado", ou no editor clássico).
                </p>
                <pre
                  data-testid="embed-iframe-code"
                  className="bg-background border border-border rounded-lg p-4 text-xs text-foreground/90 overflow-x-auto whitespace-pre-wrap"
                >{iframeHtml}</pre>
                <Button
                  className="bg-ruby hover:bg-ruby-light"
                  onClick={() => copy(iframeHtml, 'Código iframe')}
                  data-testid="embed-copy-iframe"
                >
                  <Copy className="w-4 h-4 mr-2" />Copiar HTML
                </Button>
              </TabsContent>

              <TabsContent value="wp" className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Para o plugin <strong>iframe</strong> ou <strong>Advanced iFrame</strong> no WordPress. Em alternativa,
                  usa o bloco "HTML personalizado" com o código do separador anterior.
                </p>
                <pre
                  data-testid="embed-wp-code"
                  className="bg-background border border-border rounded-lg p-4 text-xs text-foreground/90 overflow-x-auto whitespace-pre-wrap"
                >{wpShortcodeNote}</pre>
                <Button
                  variant="outline"
                  className="border-border"
                  onClick={() => copy(wpShortcodeNote, 'Shortcode')}
                >
                  <Copy className="w-4 h-4 mr-2" />Copiar shortcode
                </Button>
              </TabsContent>

              <TabsContent value="preview">
                <div className="rounded-lg overflow-hidden border border-border bg-black" style={{ height: `${height}px` }}>
                  <iframe
                    src={embedUrl}
                    width="100%"
                    height={height}
                    style={{ border: 0, display: 'block' }}
                    title="Pré-visualização do embed"
                    data-testid="embed-preview-iframe"
                  />
                </div>
              </TabsContent>
            </Tabs>

            <div className="rounded-lg bg-ruby/5 border border-ruby/20 p-4 text-xs text-muted-foreground">
              <strong className="text-foreground">Dicas:</strong>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>O iframe é responsivo (ocupa 100% da largura do container).</li>
                <li>Cliques nos contos abrem sempre num novo separador.</li>
                <li>Sem login obrigatório; sem age-gate dentro do iframe.</li>
                <li>Atualiza automaticamente quando publicas novos contos.</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
